import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: User, tenant: Tenant) => void;
  syncSession: (user: User, tenant: Tenant) => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      tenant: null,
      isAuthenticated: false,
      setAuth: (token, user, tenant) =>
        set({ token, user, tenant, isAuthenticated: true }),
      syncSession: (user, tenant) =>
        set({ token: null, user, tenant, isAuthenticated: true }),
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
      logout: () => {
        // Clear all app-specific localStorage to prevent data leaking across tenants
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('ecomflow_')) keysToRemove.push(key);
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
        try {
          const sessionKeys = Object.keys(sessionStorage).filter(key =>
            key.startsWith('ecomflow_status_expansion:') || key.startsWith('ecomflow_automation_expansion:')
          );
          sessionKeys.forEach(key => sessionStorage.removeItem(key));
        } catch {
          // Logging out must still work when session storage is unavailable.
        }
        set({ token: null, user: null, tenant: null, isAuthenticated: false });
      },
    }),
    {
      name: 'ecomflow-auth',
      // Po odświeżeniu źródłem prawdy jest httpOnly cookie. Nie utrwalamy JWT
      // w localStorage, żeby stary token nie mógł rozjechać się z nową sesją.
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
